import { convertFieldType } from '../../lib/convertV2Schema';
import {
  CGBaseTypeDefinition,
  CGBaseTypeFieldDefinition,
  CGContentType,
  CGContentTypeField,
  CGPropertyTypeField,
  CGObjectDefinition,
  OptimizelyProduct,
  CG_BASE_CONTRACT_FIELDS
} from '../../types/DataSyncItem';
import { FieldDefinition, isObjectArray } from '../../types/FieldDefinition';

export class DataSyncObjectDefinitionService {

  /**
   * Filter source fields to exclude _modified and _deleted fields when source is CGR
   */
  public static filterSourceFields(
    sourceFields: FieldDefinition[],
    sourceProduct: OptimizelyProduct
  ): FieldDefinition[] {
    if (sourceProduct === OptimizelyProduct.CGR) {
      return sourceFields.filter((field) =>
        field.name !== '_modified' && field.name !== '_deleted'
      );
    }
    return sourceFields;
  }

  public static convertToCGObject(
    sourceProduct: OptimizelyProduct,
    sourceFields: FieldDefinition[],
    sourceObjectName: string,
    destinationObjectName: string,
    baseTypeDefinitions: CGBaseTypeDefinition[],
    baseType?: string
  ): CGObjectDefinition {

    // Filter source fields to exclude _modified and _deleted fields when source is CGR
    const filteredFields = this.filterSourceFields(sourceFields, sourceProduct);

    // When a base type is selected, exclude source base contract fields from destination.
    // The correct base type fields will be added back by addRequiredBaseTypeFields.
    const baseTypeFiltered = baseType
      ? filteredFields.filter((field) => !this.isCGBaseContractField(sourceProduct, field.name))
      : filteredFields;

    // All fields from source are optional in the destination
    const processedFields = baseTypeFiltered.map((field) => ({
      ...field,
      required: false
    }));

    const cgObjectDefinition = {
        object_name: destinationObjectName,
        base_type: baseType,
        fields: processedFields,
        created_by: 'OCP',
        source_object_name: sourceObjectName,
        source_product: sourceProduct
    } as CGObjectDefinition;

    if (!baseType) {
      return cgObjectDefinition;
    }
    return this.addRequiredBaseTypeFields(cgObjectDefinition, baseTypeDefinitions, baseType);
  }

  public static convertToCGContentType(cgObjectDefinition: CGObjectDefinition): CGContentType {
    const customTypes = new Map<string, CGPropertyTypeField>();
    const processedFields: CGContentTypeField[] = [];

    // Process fields and extract custom types
    for (const cgfield of cgObjectDefinition.fields.filter((field) => !field.required)) {
      const processedField = this.processFieldForCGContentType(
        cgfield,
        cgObjectDefinition.object_name,
        customTypes
      );
      if (processedField) {
        processedFields.push(processedField);
      }
    }

    const propertyTypes = Array.from(customTypes.values());
    return {
      type_name: cgObjectDefinition.object_name,
      base_types: cgObjectDefinition.base_type ? [cgObjectDefinition.base_type] : [],
      source_product: cgObjectDefinition.source_product,
      source_object_name: cgObjectDefinition.source_object_name,
      created_by: cgObjectDefinition.created_by,
      fields: processedFields,
      property_types: propertyTypes.length > 0 ? propertyTypes : undefined
    } as CGContentType;
  }

  public static getBaseTypeFields(
    baseTypeDefinitions: CGBaseTypeDefinition[],
    baseType: string
  ): CGBaseTypeFieldDefinition[] {
    const baseTypeDefinition = baseTypeDefinitions.find((bt) => bt.name === baseType);
    return baseTypeDefinition ? baseTypeDefinition.fields : [];
  }

  public static addRequiredBaseTypeFields(
    cgObjectDefinition: CGObjectDefinition,
    baseTypeDefinitions: CGBaseTypeDefinition[],
    baseType: string
  ): CGObjectDefinition {
    const requiredFields = this.getBaseTypeFields(baseTypeDefinitions, baseType);
    const cgFields = [...cgObjectDefinition?.fields];

    for (const field of requiredFields) {
      const existingField = cgFields.find((f) => f.name.toLowerCase() === field.name.toLowerCase());
      if (!existingField) {
        cgFields.push(this.convertBaseTypeFieldToFieldDefinition(field));
      } else {
        existingField.required = true;
      }
    }

    return {
      ...cgObjectDefinition,
      fields: cgFields,
    } as CGObjectDefinition;
  }

  public static isCGBaseContractField(sourceProduct: OptimizelyProduct, fieldName: string): boolean {
    return sourceProduct === OptimizelyProduct.CGR &&
      CG_BASE_CONTRACT_FIELDS.includes(fieldName);
  }

  public static hasCGBaseContractFields(
    fields: FieldDefinition[]
  ): boolean {
    return fields.some((field) => CG_BASE_CONTRACT_FIELDS.includes(field.name));
  }

  private static convertBaseTypeFieldToFieldDefinition(
    field: CGBaseTypeFieldDefinition
  ): FieldDefinition {
    if (field.object_schema?.fields) {
      return {
        name: field.name,
        display_name: field.display_name || field.name,
        type: 'object',
        required: true,
        object_schema: {
          fields: field.object_schema.fields.map((f) => this.convertBaseTypeFieldToFieldDefinition(f))
        }
      } as FieldDefinition;
    }

    return {
      name: field.name,
      display_name: field.display_name || field.name,
      type: convertFieldType(field.type || field.primitive_type),
      required: true,
    } as FieldDefinition;
  }

  /**
   * Process a field for CG Content Type creation, handling nested objects and arrays
   */
  private static processFieldForCGContentType(
    field: FieldDefinition,
    contentTypeName: string,
    customTypes: Map<string, CGPropertyTypeField>
  ): CGContentTypeField | null {
    // Handle nested object fields
    if (field.object_schema?.fields) {
      const customTypeName = this.getCustomTypeName(contentTypeName, field);

      // Create custom type for the nested object
      const customTypeProperties: CGContentTypeField[] = [];
      for (const nestedField of field.object_schema.fields) {
        const nestedProcessedField = this.processFieldForCGContentType(
          nestedField,
          contentTypeName,
          customTypes
        );
        if (nestedProcessedField) {
          customTypeProperties.push(nestedProcessedField);
        }
      }

      if (customTypeProperties.length === 0) {
        return null;
      }

      customTypes.set(customTypeName, {
        name: customTypeName,
        fields: customTypeProperties
      });

      return {
        name: field.name,
        type: customTypeName
      };
    }

    // Handle array fields
    if (field.array) {
      if (isObjectArray(field.array)) {
        // Array of objects
        const customTypeName = this.getCustomTypeName(contentTypeName, field);

        // Create custom type for array items
        const customTypeProperties: CGContentTypeField[] = [];
        for (const arrayField of field.array.fields) {
          const arrayProcessedField = this.processFieldForCGContentType(
            arrayField,
            contentTypeName,
            customTypes
          );
          if (arrayProcessedField) {
            customTypeProperties.push(arrayProcessedField);
          }
        }

        if (customTypeProperties.length === 0) {
          return null;
        }

        customTypes.set(customTypeName, {
          name: customTypeName,
          fields: customTypeProperties
        });

        return {
          name: field.name,
          type: `[${customTypeName}]`
        };
      } else {
        // Array of primitives
        const primitiveType = this.mapToCGFieldType(field.array.type);
        return {
          name: field.name,
          type: `[${primitiveType}]`
        };
      }
    }

    // Handle primitive fields
    return {
      name: field.name,
      type: this.mapToCGFieldType(field.type)
    };
  }

  private static getCustomTypeName(ContentTypeName: string, field: FieldDefinition): string {
    const customTypeName = field.display_name ? field.display_name.replace(/\s+/g, '') : field.name;
    return `${ContentTypeName}_${customTypeName}`;
  }

  private static mapToCGFieldType(sourceFieldType?: string): string {
    const typeMap: Record<string, string> = {
      'int': 'Int',
      'integer': 'Int',
      'long': 'Int',
      'timestamp': 'DateTime',
      'date': 'Date',
      'datetime': 'DateTime',
      'boolean': 'Boolean',
      'bool': 'Boolean',
      'float': 'Float',
      'double': 'Float',
      'number': 'Float',
      'decimal': 'Float',
      'string': 'String'
    };

    if (!sourceFieldType) {
      return 'String';
    }

    return typeMap[sourceFieldType.toLowerCase()] || 'String';
  }
}
